/* eslint-disable no-console -- dev logging utility */

import { type KyanError } from "#/types/service";

export interface LogEntry {
  timestamp: string;
  level: "info" | "success" | "warning" | "error";
  category: string;
  message: string;
  data?: unknown;
}

export class Logger {
  private static logs: LogEntry[] = [];
  private static readonly MAX_LOGS = 1_000;

  private static readonly styles = {
    info: {
      emoji: "🔍",
      color: "#3b82f6",
      bgColor: "rgba(59, 130, 246, 0.1)",
    },
    success: {
      emoji: "✅",
      color: "#10b981",
      bgColor: "rgba(16, 185, 129, 0.1)",
    },
    warning: {
      emoji: "⚠️",
      color: "#f59e0b",
      bgColor: "rgba(245, 158, 11, 0.1)",
    },
    error: {
      emoji: "❌",
      color: "#ef4444",
      bgColor: "rgba(239, 68, 68, 0.1)",
    },
  };

  private static formatTimestamp(): string {
    return new Date().toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  }

  private static createLogEntry(
    level: LogEntry["level"],
    category: string,
    message: string,
    data?: unknown,
  ): LogEntry {
    return {
      timestamp: this.formatTimestamp(),
      level,
      category,
      message,
      data,
    };
  }

  private static storeLog(entry: LogEntry): void {
    this.logs.push(entry);

    if (this.logs.length > this.MAX_LOGS) {
      this.logs.shift();
    }
  }

  private static log(
    level: LogEntry["level"],
    category: string,
    message: string,
    data?: unknown,
  ): void {
    const entry = this.createLogEntry(level, category, message, data);
    this.storeLog(entry);

    const style = this.styles[level];
    const timestamp = entry.timestamp;

    // Main log with beautiful styling
    console.log(
      `%c${style.emoji} [${timestamp}] ${category.toUpperCase()} ${message}%c`,
      `color: ${style.color}; font-weight: bold; background: ${style.bgColor}; padding: 2px 8px; border-radius: 4px; border-left: 3px solid ${style.color}`,
      "color: inherit; font-weight: normal; margin-left: 8px;",
    );

    // Data section if provided
    if (data !== undefined) {
      console.log(data);
    }
  }

  // Public API methods
  static info(category: string, message: string, data?: unknown): void {
    this.log("info", category, message, data);
  }

  static success(category: string, message: string, data?: unknown): void {
    this.log("success", category, message, data);
  }

  static warning(category: string, message: string, data?: unknown): void {
    this.log("warning", category, message, data);
  }

  static error(category: string, message: string, data?: unknown): void {
    this.log("error", category, message, data);
  }

  static apiRequest(
    serviceName: string,
    payload?: unknown,
    isOneClickTradingEnabled = false,
  ): void {
    const message = `${isOneClickTradingEnabled ? "1-CT:" : ""}${serviceName}`;
    const unixTimestamp = Date.now();
    console.log(
      `%c🚀 API | ${message} | ${unixTimestamp}`,
      "color: #fff; font-weight: bold; font-size: 12px; padding: 6px 12px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 6px; text-shadow: 0 1px 2px rgba(0,0,0,0.3);",
    );
    this.info("api", `Request payload | ${serviceName}`, payload);
  }

  static apiResponse(serviceName: string, data: unknown): void {
    this.success("api", `Response data | ${serviceName}`, data);
    console.log(
      `%c🎉 REQUEST COMPLETE | ${serviceName}`,
      "color: #10b981; font-weight: bold; font-size: 10px; padding: 2px 8px; border: 1px solid #10b981; border-radius: 4px; background: rgba(16, 185, 129, 0.1);",
    );
    console.log(""); // spacing
  }

  static apiError(serviceName: string, error: KyanError): void {
    this.error("api", `Request failed | ${serviceName}`, error);
  }

  static apiValidation(serviceName: string, isValid: boolean[]): void {
    const areAllValid = isValid.every(Boolean);

    this.log(
      areAllValid ? "success" : "warning",
      "api",
      `Signature validation: ${areAllValid ? "ALL VALID" : "SOME FAILED"} | ${serviceName}`,
      isValid,
    );
  }

  // Get stored logs (useful for debugging or displaying in UI)
  static getLogs(): LogEntry[] {
    return [...this.logs];
  }

  static clearLogs(): void {
    this.logs = [];
    console.clear();
    this.info("system", "Logs cleared");
  }

  // Pretty print all logs
  static printSummary(): void {
    console.log(
      `%c📊 LOG SUMMARY (${this.logs.length} entries)`,
      "color: #fff; font-weight: bold; font-size: 14px; padding: 8px 16px; background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%); border-radius: 8px;",
    );

    const summary = this.logs.reduce<Record<string, number>>((acc, log) => {
      acc[log.level] = (acc[log.level] || 0) + 1;
      return acc;
    }, {});

    Object.entries(summary).forEach(([level, count]) => {
      const style = this.styles[level as keyof typeof this.styles];
      console.log(`${style.emoji} ${level}: ${count}`);
    });
  }
}
